<script lang="ts">
  import {
    formatCount,
    formatDurationMs,
    formatTicks,
    groupDigits,
    type MeterPoint,
    type SkipReason,
  } from '../../lib/complexity';
  import { pointStatus, reText, type PointStatus } from './chart-model';
  import { predictedOps } from './experiments';
  import Pow from './Pow.svelte';

  /** グラフと同じ値の表（components.md §3.3 A11y「表で見る」）。 */
  interface TableSeries {
    label: string;
    points: readonly MeterPoint[];
    skipped: readonly { n: number; reason: SkipReason }[];
    ops?: string | undefined;
  }

  interface Props {
    caption: string;
    series: TableSeries[];
  }

  let { caption, series }: Props = $props();

  const ns = $derived(
    [
      ...new Set(
        series.flatMap((s) => [...s.points.map((p) => p.n), ...s.skipped.map((k) => k.n)]),
      ),
    ].sort((a, b) => a - b),
  );

  type Cell =
    | { kind: 'point'; ticks: string; ms: string; ops: string | null; status: PointStatus }
    | { kind: 'skip'; text: string; ops: string | null }
    | { kind: 'none'; ops: string | null };

  function cell(s: TableSeries, n: number): Cell {
    const v = s.ops !== undefined ? predictedOps(s.ops, n) : null;
    const ops = v === null ? null : `${formatCount(v)} 回`;
    const p = s.points.find((q) => q.n === n);
    if (p) {
      const status = pointStatus(p);
      return {
        kind: 'point',
        ticks: p.ticks === null ? '記録なし' : `${formatTicks(p.ticks, p.ticksLowerBound)} 回`,
        // RE は途中で止まった時間なので、実行時間の欄には値を出さず終了コードを出す（§6.1）
        ms: status === 're' ? reText(p.exitCode) : formatDurationMs(p.durationMs, p.timedOut),
        ops,
        status,
      };
    }
    const k = s.skipped.find((q) => q.n === n);
    if (k) {
      return {
        kind: 'skip',
        text:
          k.reason === 'input-too-large'
            ? '送らず（入力が 1MiB 超）'
            : '送っていません（見積もりで 10^10 回超）',
        ops,
      };
    }
    return { kind: 'none', ops };
  }

  const showOps = $derived(series.some((s) => s.ops !== undefined));
</script>

<div class="table-scroll">
  <table class="data-table" data-testid="meter-table">
    <caption>{caption}</caption>
    <thead>
      <tr>
        <th scope="col" rowspan="2">N</th>
        {#each series as s, i (i)}
          <th scope="colgroup" colspan={showOps ? 3 : 2}>{s.label}</th>
        {/each}
      </tr>
      <tr>
        {#each series as s (s.label)}
          {#if showOps}<th scope="col">見積もり</th>{/if}
          <th scope="col">ステップ数</th>
          <th scope="col">実行時間</th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each ns as n (n)}
        <tr>
          <th scope="row" class="num">{groupDigits(n)}</th>
          {#each series as s, i (i)}
            {@const c = cell(s, n)}
            {#if showOps}<td class="num soft"
                >{#if c.ops}<Pow text={c.ops} />{:else}—{/if}</td
              >{/if}
            {#if c.kind === 'point'}
              <td class="num"><Pow text={c.ticks} /></td>
              {#if c.status === 're'}
                <td class="re" data-status="re"><span class="tle">{c.ms}</span></td>
              {:else}
                <td class="num" data-status={c.status}
                  >{#if c.status === 'tle'}<span class="tle">TLE</span>
                  {/if}{c.ms}</td
                >
              {/if}
            {:else if c.kind === 'skip'}
              <td class="soft" colspan="2"><Pow text={c.text} /></td>
            {:else}
              <td class="soft" colspan="2">—</td>
            {/if}
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<style>
  .table-scroll {
    overflow-x: auto;
  }
  .data-table {
    width: 100%;
    font-variant-numeric: tabular-nums;
  }
  caption {
    text-align: left;
    font-size: var(--fs-sm);
    color: var(--text-soft);
    padding-bottom: var(--space-1);
  }
  th,
  td {
    padding: var(--space-1) var(--space-3);
    border-bottom: 1px solid var(--border);
    text-align: right;
    font-size: var(--fs-sm);
    white-space: nowrap;
  }
  thead th {
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
  }
  thead th[scope='colgroup'] {
    color: var(--text);
    text-align: center;
  }
  tbody th {
    text-align: left;
  }
  .soft {
    color: var(--text-soft);
  }
  .tle {
    font-weight: var(--fw-bold);
    font-size: var(--fs-xs);
  }
</style>
